import { useCallback, useRef, useState, type ReactNode } from "react";
import type { RenderBlock, RenderPlan } from "@pushback-and-chaos/documents";
import { DocumentBlock } from "../../documents/reader.js";
import type { PageItem } from "./pages.js";

/**
 * A DOCUMENT, CUT INTO PAGES THAT LOSE NOTHING.
 *
 * The rule the whole desk rests on: pages are made by SLICING THE DATA and
 * handing the slice back to the one renderer (`DocumentBlock`). Page two of a
 * board-pack extract is the same component, the same right-set tabular
 * numerals, the same column headings — because it IS the same table, with
 * fewer rows. Nothing here re-implements a block role, rasterises words, or
 * flattens a document to prose.
 *
 * Long tables become ROW PAGES with their headings repeated. Wide tables
 * become COLUMN GROUPS, each carrying the row-label column so a figure never
 * loses the row it belongs to, and each saying which columns it is showing.
 * A footnote list continued on a later page keeps counting.
 */

/** Which columns of a wide table a page group shows. Empty means "all of them". */
export type ColumnGroups = ReadonlyMap<string, readonly (readonly number[])[]>;

export function documentItems(plan: RenderPlan, groups: ColumnGroups): readonly PageItem[] {
  const items: PageItem[] = [];
  for (const [index, block] of plan.blocks.entries()) {
    const key = `b${String(index)}:${block.role}`;
    items.push(...itemsForBlock(key, block, groups.get(key) ?? null));
  }
  return items;
}

function itemsForBlock(key: string, block: RenderBlock, groups: readonly (readonly number[])[] | null): readonly PageItem[] {
  switch (block.role) {
    case "header":
      return [{ key, kind: "block", node: () => <DocumentBlock block={block} /> }];

    case "paragraph":
      return [{ key, kind: "text", text: block.text, className: "pcs-doc-p" }];

    case "signature":
      return [{ key, kind: "text", text: block.text, className: "pcs-doc-p" }];

    case "banner":
      return [{ key, kind: "text", text: block.text, className: "pcs-doc-banner" }];

    case "annotation":
      return [
        {
          key,
          kind: "text",
          text: block.text,
          className: "pcs-doc-note",
          tail: <span className="mt-1 block text-micro opacity-70">{` — ${block.author}`}</span>,
        },
      ];

    case "redaction":
      return [
        {
          key,
          kind: "text",
          text: block.text,
          className: "pcs-doc-redaction",
          lead: (
            <>
              <span className="bg-ink px-1 text-ink">████</span>{" "}
            </>
          ),
        },
      ];

    // A transcript turn reads as speaker-above-words on a desk this wide and
    // this short; the eight-rem speaker column of the floating reader would
    // take a quarter of the panel. The attribution is still explicit, still
    // beside its own words, and the speech can now be cut across pages.
    case "transcript-turn":
      return [
        { key: `${key}:who`, kind: "block", node: () => <p className="pcs-doc-speaker">{block.speaker}</p> },
        { key, kind: "text", text: block.text, className: "pcs-doc-p" },
        ...(block.nonVerbal === null
          ? []
          : [{ key: `${key}:nv`, kind: "block" as const, node: () => <p className="pcs-doc-nonverbal">[{block.nonVerbal}]</p> }]),
      ];

    case "email-header":
      return [{ key, kind: "block", node: () => <DocumentBlock block={block} /> }];

    case "conflict-pair":
      return [
        {
          key,
          kind: "units",
          units: 3,
          // The first source, the second, and the line that refuses to
          // adjudicate. A pair too tall for a short panel is read across two
          // pages — never drawn twice, which is what happened when this node
          // ignored the slice it was handed (found on the real Harborlight
          // board pack at 1366x768: the same disagreement on pages 3 and 5).
          node: (from, to) => <DocumentBlock block={block} cells={[0, 1, 2].slice(from, to ?? undefined)} />,
        },
      ];

    case "kv-pair":
      return [
        {
          key,
          kind: "units",
          units: block.pairs.length,
          node: (from, to) => <DocumentBlock block={{ ...block, pairs: block.pairs.slice(from, to ?? undefined) }} />,
        },
      ];

    case "letterhead":
      return [
        {
          key,
          kind: "units",
          units: block.pairs.length,
          node: (from, to) => <DocumentBlock block={{ ...block, pairs: block.pairs.slice(from, to ?? undefined) }} />,
        },
      ];

    case "footnote-list":
      return [
        {
          key,
          kind: "units",
          units: block.items.length,
          // The numbering continues; a footnote is a reference, and "(1)" on
          // two different pages would break every citation in the document.
          node: (from, to) => <DocumentBlock block={{ ...block, items: block.items.slice(from, to ?? undefined) }} startIndex={from} />,
        },
      ];

    case "attachment-list":
      return [
        {
          key,
          kind: "units",
          units: block.items.length,
          node: (from, to) => <DocumentBlock block={{ ...block, items: block.items.slice(from, to ?? undefined) }} />,
        },
      ];

    case "table":
    case "sheet-table": {
      const columnCount = block.columns.length;
      const chosen = groups === null || groups.length === 0 ? [range(columnCount)] : groups;
      /*
       * Three atoms per column group, not one: the sheet's TITLE and its
       * PROVENANCE STRIP are drawn once each, and only the column headings
       * repeat. Folding them into the rows meant every continuation redrew a
       * title, a units line, two heading rows and a source line — about
       * 130px of a desk that is 190px tall on a phone, which left no room
       * for the figures the page exists to show.
       *
       * A wide table is cut into column groups FIRST, and each group says
       * which columns it is showing and carries the row labels, so a figure
       * can never be read against the wrong row or the wrong month.
       */
      return chosen.flatMap((columns, groupIndex) => {
        const base = chosen.length === 1 ? key : `${key}:c${String(groupIndex)}`;
        const cut = withColumns(block, columns, 0, null);
        const note = chosen.length === 1 ? undefined : `${columnLabel(block, columns)} — ${String(columns.length)} of ${String(columnCount)} columns`;
        const rows: PageItem = {
          key: base,
          kind: "units" as const,
          units: block.rows.length,
          node: (from: number, to: number | null) => <DocumentBlock block={withColumns(block, columns, from, to)} startIndex={from} part="rows" />,
        };
        if (block.role === "table") return [rows];
        return [
          { key: `${base}:head`, kind: "block" as const, node: () => <DocumentBlock block={cut} note={note} part="head" /> },
          rows,
          { key: `${base}:foot`, kind: "block" as const, node: () => <DocumentBlock block={cut} part="foot" /> },
        ];
      });
    }

    default:
      return assertNever(block);
  }
}

function range(count: number): readonly number[] {
  return Array.from({ length: count }, (_, index) => index);
}

type TableBlock = Extract<RenderBlock, { role: "table" } | { role: "sheet-table" }>;

/** The same table with a subset of its columns and a slice of its rows — the ROW LABEL always kept. */
function withColumns(block: TableBlock, columns: readonly number[], from: number, to: number | null): RenderBlock {
  const pick = <T,>(values: readonly T[]): T[] => columns.flatMap((index) => { const value = values[index]; return value === undefined ? [] : [value]; });
  if (block.role === "table") {
    return {
      ...block,
      columns: pick(block.columns),
      rows: block.rows.slice(from, to ?? undefined).map((row) => ({ ...row, values: pick(row.values) })),
    };
  }
  return {
    ...block,
    columns: pick(block.columns),
    rows: block.rows.slice(from, to ?? undefined).map((row) => ({ ...row, values: pick(row.values) })),
  };
}

/**
 * Which columns a group is showing, in the words the reader can match against
 * what is in front of them.
 *
 * A sheet is named by its LETTERS, because the letters are drawn across the
 * top of it and because a sheet's labels repeat ("Revenue", "Revenue") in a
 * way that makes a range of them meaningless — "Revenue to Revenue" was the
 * first thing this produced. A plain table has no letters, so it is named by
 * its headings.
 */
function columnLabel(block: TableBlock, columns: readonly number[]): string {
  const first = columns[0];
  const last = columns.at(-1);
  if (first === undefined || last === undefined) return "columns";
  const name = (index: number): string => {
    const column = block.columns[index];
    if (column === undefined) return String(index + 1);
    return typeof column === "string" ? column : column.letter;
  };
  if (block.role === "sheet-table") {
    return first === last ? `column ${name(first)}` : `columns ${name(first)} to ${name(last)}`;
  }
  const heading = (index: number): string => block.columns[index] ?? String(index + 1);
  return first === last ? heading(first) : `${heading(first)} to ${heading(last)}`;
}

function assertNever(block: never): never {
  throw new Error(`documentItems: unhandled block role ${JSON.stringify(block)}`);
}

/**
 * WIDE TABLES.
 *
 * Whether a table is too wide is a question only real layout can answer — it
 * depends on the face, the figures and the panel. So the off-screen column is
 * asked: it holds the full table at the visible width, and its heading cells
 * report their natural widths. When they do not fit, the columns are packed
 * into groups that do, each group repeating the row-label column so a figure
 * never loses the row it belongs to. A table that fits is left alone and
 * never labelled.
 *
 * TWO PASSES PER WIDTH, and exactly two. The decision changes what is
 * rendered, and what is rendered is what gets measured, so a naive loop
 * measures its own answer and oscillates for ever (it did: "Maximum update
 * depth exceeded", found at 1440x900 on the review bench). The width the
 * decision was made at is therefore remembered, and no further pass at that
 * width may revisit it.
 *
 * `contentKey` is the other half of that, and it is not optional: a window
 * measures ITSELF before its render plan has arrived, when the only thing on
 * the column is the word "Reading it.". A decision settled against that would
 * stand for ever, and the table that arrived a moment later would run off the
 * side of the paper — which is exactly what it did, on the real Harborlight
 * board pack. A new content key reopens the question.
 */
export function useColumnGroups(contentKey: string): { readonly groups: ColumnGroups; readonly onMeasured: (root: HTMLElement, width: number) => void } {
  const [groups, setGroups] = useState<ColumnGroups>(() => new Map());
  const held = useRef<ColumnGroups>(groups);
  held.current = groups;
  const decision = useRef<{ width: number; key: string; settled: boolean }>({ width: Number.NaN, key: "", settled: false });
  const content = useRef(contentKey);
  content.current = contentKey;

  const onMeasured = useCallback((root: HTMLElement, width: number) => {
    const key = content.current;
    if (!(Math.abs(decision.current.width - width) < 0.5) || decision.current.key !== key) {
      decision.current = { width, key, settled: false };
      // A decision can only be made from the FULL table. If groups are
      // already in force, clear them and let that re-render bring the full
      // table back for the next pass; if there are none, the column in front
      // of us is already the full one and the decision can be made now.
      if (held.current.size > 0) {
        setGroups(new Map());
        return;
      }
    }
    if (decision.current.settled) return;
    decision.current = { width, key, settled: true };

    const next = new Map<string, readonly (readonly number[])[]>();
    for (const atom of root.querySelectorAll<HTMLElement>("[data-pcs-atom]")) {
      const key = atom.dataset["pcsAtom"];
      if (key === undefined) continue;
      const table = atom.querySelector("table");
      if (table === null) continue;
      const heads = Array.from(table.querySelectorAll<HTMLElement>("thead tr:last-child > th"));
      if (heads.length < 3) continue;
      const labelWidth = heads[0]?.getBoundingClientRect().width ?? 0;
      const widths = heads.slice(1).map((head) => head.getBoundingClientRect().width);
      const natural = labelWidth + widths.reduce((total, w) => total + w, 0);
      if (natural <= width + 1) continue;

      const packed: number[][] = [];
      let current: number[] = [];
      let used = labelWidth;
      for (const [index, columnWidth] of widths.entries()) {
        if (current.length > 0 && used + columnWidth > width) {
          packed.push(current);
          current = [];
          used = labelWidth;
        }
        current.push(index);
        used += columnWidth;
      }
      if (current.length > 0) packed.push(current);
      if (packed.length > 1) next.set(key, packed);
    }
    setGroups((previous) => (sameGroups(previous, next) ? previous : next));
  }, []);

  return { groups, onMeasured };
}

function sameGroups(a: ColumnGroups, b: ColumnGroups): boolean {
  if (a.size !== b.size) return false;
  for (const [key, groups] of a) {
    const other = b.get(key);
    if (other === undefined || other.length !== groups.length) return false;
    for (let i = 0; i < groups.length; i += 1) {
      if ((groups[i] ?? []).join(",") !== (other[i] ?? []).join(",")) return false;
    }
  }
  return true;
}

/** The document's title, drawn once above the pages — never counted as content. */
export function DocumentTitle({ title }: { title: string }): ReactNode {
  return <h3 className="pcs-reader-title">{title}</h3>;
}
